/* eslint-disable security/detect-object-injection */
import {
  Box,
  Group,
  Text,
  LoadingOverlay,
  useMantineColorScheme,
  ActionIcon,
} from "@mantine/core"
import { IconRefresh } from "@tabler/icons-react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import dayjs from "dayjs"
import { Page } from "../../../../-components/Page"
import {
  CastDto,
  DepartmentScheduleData,
  FormulaResult,
  PersonStatusDto,
} from "../../../../@client/Theasoft_ApiServer_Orchestra"
import {
  EditCastModal,
  EditingCast,
} from "./-components/modals/edit-cast-modal"
import { MatrixCssGrid } from "./-components/matrix-css-grid"
import { ErrorAlert } from "../../../../blocks/errors/error-alert"
import { CastCell } from "./-components/cells/cast-cell"
import { useTranslation } from "react-i18next"
import {
  ActivityCellCoordinate,
  AddCastModal,
} from "./-components/modals/add-cast-modal"
import { useSuspenseQueryGet } from "../../../../typed-client/hooks/use-suspense-query-get"
import { useQueryGet } from "../../../../typed-client/hooks/use-query-get"
import {
  WorkAreaStatisticsCell,
  WorkareaHeaderCell,
} from "./-components/cells/workarea-target-cell"
import { match } from "ts-pattern"
import {
  Dictionary,
  List,
} from "../../../../@client/System_Collections_Generic"
import { DayHeaderCell } from "./-components/cells/day-header-cell"
import {
  ActivityOrPeriodHeaderCell,
  ActivityOrDayChangeOrFormula,
  DataXGroup as DayActivityGroup,
  DataX_Activity,
  DataX_DayChange,
  DataX_Sum,
} from "./-components/cells/activity-or-period-header-cell"
import {
  WorkareaOrContract,
  WorkareaOrContractHeaderCell,
} from "./-components/cells/workarea-or-contract-header-cell"
import { FormulaContractCell } from "./-components/cells/formula-contract-cell"
import { DayBorderCell } from "./-components/cells/day-border-cell"
import { DateOnly } from "../../../../@client/System"
import { Filter } from "./-components/filter"
import { keepPreviousData, useQueryClient } from "@tanstack/react-query"
import { dateAndTimeOnlyToDateTime } from "../../../../utils/date-only-to-date-time"
import { usePostMutation } from "../../../../typed-client/hooks/use-post-mutation"
import { showErrorNotification } from "../../../../utils/form-submission-notifications"
import { mergeDepartmentScheduleData } from "./-components/merge-department-schedule"
import { DepartmentSelect } from "./-components/orchestra-select"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { useDebouncedValue, useSessionStorage } from "@mantine/hooks"
import { ProductionSelect } from "./-components/production-select"
import { SettingsMenu } from "./-components/settings-menu"
import { PeriodSelect } from "./-components/period-select"
import { PrintCastButton } from "./-components/print-cast-button"
import {
  AbsenceCellCoordinate,
  AddAbsenceModal,
} from "./-components/modals/add-absence-modal"
import { EditAbsenceModal } from "./-components/modals/edit-absence-modal"
import { z } from "zod"
import { dateToDateOnly } from "@/utils/date-to-date-only"

const schema = z.coerce.number().optional()

type SearchParams = {
  departmentId?: number
}

export const Route = createFileRoute("/app/duties/orchestra/")({
  component: OrchestraPage,
  beforeLoad(ctx) {
    return { getTitle: () => ctx.context.t && ctx.context.t("Orchestra") }
  },
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    departmentId: schema.parse(search.departmentId),
  }),
})

function getCellData(
  columnId: string,
  contractId: number,
  data: DepartmentScheduleData
) {
  const id = `${columnId}__${contractId}`
  return data.cells[id]
}

function getPersonStatus(personStatusId: number, data: DepartmentScheduleData) {
  return data.personStatuses[personStatusId]
}

function getWorkAreaTargetsAndSums(
  activityId: number,
  workAreaId: number,
  data: DepartmentScheduleData
) {
  return data.workAreaNeeds.find(
    ([vActivityId, vWorkAreaId]) =>
      vActivityId === activityId && vWorkAreaId === workAreaId
  )?.[2]
}

function getFormulaSum(
  contractId: number,
  formulaId: string,
  data: DepartmentScheduleData
): FormulaResult | undefined {
  return data.formulaResults.find(
    ([contract, formula]) =>
      contract.contractId == contractId && formula.id == formulaId
  )?.[2]
}

const emptyScheduleData: DepartmentScheduleData = {
  cells: {},
  personStatuses: {},
  availableAbsenceShiftTypes: [],
  dayColumns: [],
  workareasAndContracts: [],
  workareas: [],
  castByContractIdAndActivityId: {},
  formulas: [],
  department: {
    permission: "ReadOnly",
    value: { departmentId: 0, name: "" },
    hasDepartmentWritePermission: false,
  },
  formulaResults: [],
  workAreaNeeds: [],
  period: { end: "0-0-0T0:0:0", start: "0-0-0T0:0:0" },
}

function selectData(data: DepartmentScheduleData | undefined) {
  const scheduleData = data || emptyScheduleData

  const formulaDayChangesAndActivities = [
    ...scheduleData.formulas.map(
      (v) =>
        ({
          type: "formula",
          data: v,
          isFirst: true,
          width: 35,
          group: null,
        }) satisfies DataX_Sum
    ),
    ...scheduleData.dayColumns.flatMap((day) => {
      return day.columns.flatMap((c) => {
        return [
          ...(c.isFirst
            ? [
                {
                  type: "day_change",
                  width: 1,
                  isFirst: true,
                  group: {
                    day: dayjs(day.day),
                    span: day.columns.length,
                  },
                } satisfies DataX_DayChange,
              ]
            : []),
          {
            type: "activity",
            data: c,
            isFirst: false,
            width: 70,
            group: {
              day: dayjs(day.day),
              span: day.columns.length,
            },
          } satisfies DataX_Activity,
        ]
      })
    }),
  ]
  const dayActivityGroups: DayActivityGroup[] = scheduleData.dayColumns.map(
    (v) => ({
      day: dayjs(v.day),
      span: v.columns.length,
    })
  )
  const workareasAndContrats = scheduleData.workareasAndContracts.flatMap(
    ([workArea, workareaContracts]) => {
      const workarea: WorkareaOrContract = {
        type: "workarea",
        data: workArea.value,
        permission: workArea.permission,
        workareaId: workArea.value.workAreaId,
      }
      const contracts: WorkareaOrContract[] = workareaContracts.map((v) => ({
        type: "contract",
        permission: workArea.permission,
        data: v,
        workareaId: v.workArea.workAreaId,
      }))
      return [workarea, ...contracts]
    }
  )
  return {
    scheduleData,
    dataX: formulaDayChangesAndActivities,
    xGroups: dayActivityGroups,
    dataY: workareasAndContrats,
  }
}

const today = dateToDateOnly(dayjs().toDate())
const todayPlus7 = dateToDateOnly(dayjs().add(7, "d").toDate())

function OrchestraPage() {
  const { data: currentSeason } = useSuspenseQueryGet(
    "/api/Season/GetCurrentSeason",
    null
  )
  const { colorScheme } = useMantineColorScheme()
  const { data: orchesters } = useSuspenseQueryGet(
    "/api/Orchestra/GetOrchesters",
    null
  )
  const { departmentId } = Route.useSearch()
  const client = useQueryClient()

  const selectedOrchester = departmentId

  const navigate = useNavigate()
  React.useEffect(() => {
    if (departmentId === undefined && orchesters.length !== 0) {
      navigate({
        to: "/app/duties/orchestra",
        search: { departmentId: orchesters[0].departmentId },
        replace: true,
      })
    }
  }, [departmentId, orchesters, navigate])
  // const [[intermediateStartDate, intermediateEndDate], setDateRange] =
  //   useSessionStorage<[DateOnly | null, DateOnly | null]>({
  //     key: "orchestra/selected-period",
  //     defaultValue: [today, todayPlus7] as [DateOnly | null, DateOnly | null],
  //   })

  const [storedDateRange, setStoredDateRange] = useSessionStorage<
    [DateOnly, DateOnly] | null
  >({
    key: "orchestra/selected-period",
    defaultValue: null,
    getInitialValueInEffect: false,
  })

  const [[intermediateStartDate, intermediateEndDate], setDateRange] =
    React.useState<[DateOnly | null, DateOnly | null]>(
      storedDateRange ?? [today, todayPlus7]
    )

  const [[startDate, endDate], setLastValidDateRange] = React.useState<
    [DateOnly, DateOnly]
  >([today, todayPlus7])

  React.useEffect(() => {
    if (intermediateStartDate && intermediateEndDate) {
      setLastValidDateRange([intermediateStartDate, intermediateEndDate])
      if (!selectedProduction) {
        setStoredDateRange([intermediateStartDate, intermediateEndDate])
      }
    }
  }, [intermediateStartDate, intermediateEndDate])

  const [showCanceledActivities, setShowCanceledActivities] =
    React.useState(false)

  const [selectedWorkareaIds, setCurrentSelectedWorkareaIds] =
    useUserLocalStorage<number[] | null>({
      key: `orchester/${selectedOrchester || ``}/filtered-tworkarea-ids`,
      defaultValue: null,
    })
  const [debouncedSelectedWorkareaIds] = useDebouncedValue(
    selectedWorkareaIds,
    1000
  )

  const getScheduleEnabled =
    debouncedSelectedWorkareaIds !== undefined &&
    selectedOrchester !== null &&
    selectedOrchester !== undefined

  const [selectedProduction, setSelectedProduction] = React.useState<
    number | null
  >(null)
  const queryKey = React.useMemo(
    () =>
      ({
        start: startDate,
        end: endDate,

        //cast to number as query is not run if no orchester is selected
        departmentId: selectedOrchester as number,

        workAreaIds:
          debouncedSelectedWorkareaIds === null
            ? []
            : debouncedSelectedWorkareaIds,

        showCanceledActivities,
        productionId: selectedProduction,
      }) as const,
    [
      startDate,
      endDate,
      selectedOrchester,
      debouncedSelectedWorkareaIds,
      selectedProduction,
    ]
  )

  const { data, isFetching, refetch, error } = useQueryGet(
    "/api/Orchestra/GetSchedule",
    queryKey,
    {
      placeholderData: keepPreviousData,
      enabled: getScheduleEnabled,
    }
  )

  const getRowAndCell = usePostMutation(
    "/api/Orchestra/GetActivityContractCell",
    {
      onSuccess: (_, v) => {
        removeLoadingCell({
          activityId: v.activityId,
          contractId: v.contractId,
        })
      },
      onSettled: (newData) => {
        if (newData) {
          // not type safe
          client.setQueryData(
            ["/api/Orchestra/GetSchedule", queryKey],
            (oldData: DepartmentScheduleData) => {
              return mergeDepartmentScheduleData(oldData, newData)
            }
          )
        }
      },
    }
  )

  function reloadCellData({
    activityId,
    contractId,
    departmentId,
    workareaIds,
  }: {
    activityId: number
    contractId: number
    departmentId: number
    workareaIds: number[]
  }) {
    getRowAndCell.mutate({
      activityId,
      contractId,
      departmentId,
      workareaIds,
      showCanceledActivities,
    })
  }
  const [loadingCells, setLoadingCells] = React.useState<
    ActivityCellCoordinate[]
  >([])

  function setCellLoading({
    activityId,
    contractId,
    workareaId,
  }: {
    activityId: number
    contractId: number
    workareaId: number
  }) {
    setLoadingCells((v) => {
      return [
        ...v,
        {
          activityId: activityId,
          contractId: contractId,
          type: "activity-cell",
          workareaId: workareaId,
        } satisfies ActivityCellCoordinate,
      ]
    })
  }
  function removeLoadingCell({
    contractId,
    activityId,
  }: {
    contractId: number
    activityId: number
  }) {
    setLoadingCells((v) => {
      return v.filter(
        (x) => !(x.contractId === contractId && x.activityId === activityId)
      )
    })
  }

  const quickDeleteCast = usePostMutation("/api/Orchestra/DeleteCast", {
    onSettled: () => {
      refetch()
    },
  })
  const quickAddCast = usePostMutation("/api/Orchestra/QuickAddCast", {
    onError: (v) => {
      if (v.message == "Orchestra_QuickAddError_MultiplePossibleRoles") {
        // no op
      } else {
        showErrorNotification(v)
      }
    },
    onMutate: (req) => {
      setCellLoading(req)
    },
    onSettled: (_, __, request) => {
      if (selectedOrchester) {
        reloadCellData({
          activityId: request.activityId,
          contractId: request.contractId,
          departmentId: selectedOrchester,
          workareaIds: [request.workareaId],
        })
      }
    },
  })

  const { scheduleData, dataX, xGroups, dataY } = React.useMemo(
    () => selectData(data),
    [data]
  )

  const [editingAbsence, setEditingAbsence] =
    React.useState<null | PersonStatusDto>(null)

  const [addingAbsence, setAddingAbsence] =
    React.useState<null | AbsenceCellCoordinate>(null)

  const [adding, setAdding] = React.useState<null | ActivityCellCoordinate>(
    null
  )

  const [editing, setEditing] = React.useState<null | EditingCast>(null)

  const { t } = useTranslation("app_server")

  const availableAbsenceShiftTypes = data?.availableAbsenceShiftTypes ?? []

  const showCastStatus = React.useState(false)

  const quickDelete = useUserLocalStorage({
    key: "orchestra/quick-delete",
    defaultValue: false,
  })
  const quickAdd = useUserLocalStorage({
    key: "orchestra/quick-add",
    defaultValue: false,
  })

  if (orchesters.length === 0) {
    return (
      <Page>
        <ErrorAlert error={t("NoOrchestrasFound")} />
      </Page>
    )
  }
  if (selectedOrchester === undefined) {
    return (
      <Page>
        <ErrorAlert error={t("NoOrchestraSelected")} />
      </Page>
    )
  }
  return (
    <Page
      crumbOverride={() => (
        <Group
          key="custom-orchestra-breadcrumb"
          justify="space-between"
          w="100%"
          flex={1}
        >
          <DepartmentSelect
            value={selectedOrchester?.toString() || null}
            onChange={(v) => {
              if (v) {
                navigate({
                  to: "/app/duties/orchestra",
                  search: { departmentId: parseInt(v) },
                })
              }
            }}
            data={orchesters.map((v) => ({
              label: v.name,
              value: v.departmentId.toString(),
            }))}
          />

          {selectedOrchester ? (
            <ProductionSelect
              departmentId={selectedOrchester}
              value={selectedProduction}
              start={startDate}
              end={endDate}
              onChange={(v) => {
                if (v === null) {
                  setDateRange([today, todayPlus7])
                  setSelectedProduction(null)
                } else {
                  if (currentSeason) {
                    setDateRange([today, currentSeason.endDate])
                  }
                  setSelectedProduction(v)
                }
              }}
            />
          ) : null}
        </Group>
      )}
    >
      <ErrorAlert error={error} />
      <AddAbsenceModal
        departmentId={selectedOrchester}
        coordinate={addingAbsence}
        onAdded={() => {
          setAddingAbsence(null)
          refetch()
        }}
        onCancel={() => {
          setAddingAbsence(null)
        }}
      />
      <EditAbsenceModal
        departmentId={selectedOrchester}
        personStatus={editingAbsence}
        onEdited={() => {
          setEditingAbsence(null)
          refetch()
        }}
        onCancel={() => {
          setEditingAbsence(null)
        }}
      />
      <AddCastModal
        departmentId={departmentId!}
        coordinate={adding}
        onCancel={() => {
          setAdding(null)
        }}
        onAdded={() => {
          // refetch()
          if (selectedOrchester && adding) {
            setCellLoading({
              activityId: adding.activityId,
              contractId: adding.contractId,
              workareaId: adding.workareaId,
            })
            reloadCellData({
              activityId: adding.activityId,
              contractId: adding.contractId,
              departmentId: selectedOrchester,
              workareaIds: [adding.workareaId],
            })
          }
          setAdding(null)
        }}
      />
      <EditCastModal
        coordinate={editing}
        onCancel={() => {
          setEditing(null)
        }}
        onDeleted={() => refetch()}
        onEdited={() => {
          if (selectedOrchester && editing) {
            setCellLoading({
              activityId: editing.activityId,
              contractId: editing.contractId,
              workareaId: editing.workareaId,
            })

            reloadCellData({
              activityId: editing.activityId,
              contractId: editing.contractId,
              departmentId: selectedOrchester,
              workareaIds: [editing.workareaId],
            })
          }
          setEditing(null)
        }}
      />
      <Group justify="space-between">
        <Group gap={4}>
          <PeriodSelect
            value={[intermediateStartDate, intermediateEndDate]}
            onChange={(v) => setDateRange(v)}
          />
        </Group>
        <Group gap={4}>
          <ActionIcon variant="default" onClick={() => refetch()}>
            <IconRefresh size={16} />
          </ActionIcon>
          {selectedOrchester !== null ? (
            <Filter
              selectedProduction={selectedProduction}
              setSelectedProduction={setSelectedProduction}
              selectedWorkareaIds={selectedWorkareaIds}
              key={selectedOrchester}
              onSetSelectedWorkareaIds={(selectedWorkareaIds) => {
                setCurrentSelectedWorkareaIds(selectedWorkareaIds ?? null)
              }}
              showCanceledActivities={showCanceledActivities}
              setShowCanceledActivities={setShowCanceledActivities}
              workareas={data?.workareas.map((v) => v.value) || []}
            />
          ) : null}
          <SettingsMenu
            quickAdd={quickAdd}
            quickDelete={quickDelete}
            showCastStatus={showCastStatus}
          />

          {selectedOrchester ? (
            <PrintCastButton
              filteredWorkareaIds={selectedWorkareaIds}
              departmentId={selectedOrchester}
              start={dateAndTimeOnlyToDateTime(startDate, "00:00:00")}
              end={dateAndTimeOnlyToDateTime(endDate, "23:59:59")}
            />
          ) : null}
        </Group>
      </Group>
      <Box
        bg={colorScheme == "dark" ? "dark.5" : "gray.2"}
        mt="xs"
        p={1}
        style={{
          position: "relative",
          alignSelf: "start",
          maxWidth: "100%",
          overflow: "auto",
        }}
      >
        <MatrixCssGrid<
          ActivityOrDayChangeOrFormula,
          DayActivityGroup,
          WorkareaOrContract
        >
          getGroup={(x) => x.group}
          isFirstInGroup={(x) => x.isFirst}
          dataX={dataX}
          dataXGroups={xGroups}
          noDataX={
            isFetching || !getScheduleEnabled ? null : (
              <Text key="no-activities" c="red" m="xs" fw={700}>
                {t("NoActivitiesInSelectedPeriod")}
              </Text>
            )
          }
          noDataY={
            isFetching || !getScheduleEnabled ? null : (
              <Text key="no-instruments" c="red" m="xs" fw={700}>
                {t("NoInstrumentsInSelectedPeriod")}
              </Text>
            )
          }
          xHeaderGroup={(group, orientation, i) => (
            <DayHeaderCell key={i} day={group.day} orientation={orientation} />
          )}
          xHeader={(x, orientation, i) =>
            departmentId ? (
              <ActivityOrPeriodHeaderCell
                departmentId={departmentId}
                key={i}
                x={x}
                orientation={orientation}
              />
            ) : null
          }
          yHeader={(y, orientation, i) => (
            <WorkareaOrContractHeaderCell
              key={i}
              y={y}
              orientation={orientation}
            />
          )}
          dataY={dataY}
          renderCell={(x, y, xId, yId) => {
            const workareaId = y.workareaId
            const key = `${xId}-${yId}`
            return match({ x, y })
              .with(
                { x: { type: "activity" }, y: { type: "workarea" } },
                ({ x: activity, y: _ }) => {
                  const activityId = activity.data.activityDto
                    ? activity.data.activityDto.activityId
                    : undefined
                  const targetSum = activityId
                    ? getWorkAreaTargetsAndSums(
                        activityId,
                        workareaId,
                        scheduleData
                      ) || null
                    : null
                  return (
                    <WorkAreaStatisticsCell
                      key={key}
                      data={targetSum || undefined}
                    />
                  )
                }
              )
              .with(
                { x: { type: "activity" }, y: { type: "contract" } },
                ({ x, y }) => {
                  const { activityDto: activity, columnId } = x.data
                  const cell = getCellData(
                    columnId,
                    y.data.contractId,
                    scheduleData
                  )
                  const personStatuses =
                    cell?.personStatusIds.flatMap((id) =>
                      getPersonStatus(id, scheduleData)
                    ) ?? []

                  const contractId = y.data.contractId

                  const contractEntries: Dictionary<number, List<CastDto>> = (
                    scheduleData.castByContractIdAndActivityId as any
                  )[contractId] // TODO: improve typing
                  const activityId = activity ? activity.activityId : null
                  const cast =
                    contractEntries && activityId
                      ? contractEntries[activityId] || null
                      : null

                  const targetSum = x.data.activityDto
                    ? getWorkAreaTargetsAndSums(
                        x.data.activityDto.activityId,
                        y.workareaId,
                        scheduleData
                      ) || undefined
                    : undefined

                  const selected =
                    (editing &&
                      editing.contractId === contractId &&
                      cast?.some((v) => v.castId == editing.castId)) ||
                    (adding &&
                      adding.contractId === contractId &&
                      adding.activityId === activityId) ||
                    false

                  const isLoading = loadingCells.some(
                    (v) =>
                      v.contractId == contractId &&
                      v.activityId == activityId &&
                      v.type === "activity-cell"
                  )

                  const productionAllowed = y.data.productionRestricted
                    ? activity &&
                      y.data.prodcutionIds.includes(activity.productionId)
                    : true

                  return (
                    <CastCell
                      hasDepartmentWritePermission={
                        data?.department.hasDepartmentWritePermission || false
                      }
                      productionAllowed={productionAllowed}
                      personStatuses={personStatuses}
                      key={key}
                      canAdd={
                        productionAllowed && y.permission === "ReadAndWrite"
                      }
                      canModify={
                        productionAllowed && y.permission === "ReadAndWrite"
                      }
                      quickAdd={quickAdd[0]}
                      quickDelete={quickDelete[0]}
                      isFirstInGroup={x.isFirst}
                      isLoading={isLoading}
                      showCastStatus={showCastStatus[0]}
                      selected={selected}
                      expectedWorkareaCasts={targetSum?.roleCountTarget ?? 0}
                      noTarget={
                        targetSum === undefined ||
                        targetSum?.roleCountTarget === 0
                      }
                      cell={cell}
                      personId={y.data.person.personId}
                      contractId={y.data.contractId}
                      cast={cast}
                      activityId={activityId}
                      availableAbsenceShiftTypes={availableAbsenceShiftTypes}
                      onQuickDeleteClicked={({ castId }) => {
                        quickDeleteCast.mutate({ castId })
                      }}
                      onEditAbsenceClicked={(v) => {
                        setEditingAbsence(v)
                      }}
                      onAddAbsenceClicked={(v) => {
                        setAddingAbsence({
                          ...v,
                          type: "absence-cell",
                          date: x.data.date,
                        })
                      }}
                      onQuickCreateCastClicked={(v) => {
                        quickAddCast.mutate(
                          {
                            activityId: v.activityId,
                            contractId: v.contractId,
                            workareaId: workareaId,
                          },
                          {
                            onError: (e) => {
                              if (
                                e.message ==
                                "Orchestra_QuickAddError_MultiplePossibleRoles"
                              ) {
                                setAdding({
                                  type: "activity-cell",
                                  workareaId,
                                  activityId: v.activityId,
                                  contractId: v.contractId,
                                })
                              }
                            },
                          }
                        )
                      }}
                      onCreateCastClicked={({ contractId, activityId }) => {
                        setAdding({
                          type: "activity-cell",
                          activityId,
                          contractId,
                          workareaId: workareaId,
                        })
                      }}
                      onCastClicked={({ cast, contractId, activityId }) => {
                        setEditing({
                          type: "edit",
                          castId: cast.castId,
                          contractId: contractId,
                          activityId,
                          workareaId,
                        })
                      }}
                    />
                  )
                }
              )
              .with({ x: { type: "day_change" } }, () => (
                <DayBorderCell key={key} />
              ))
              .with({ x: { type: "formula" }, y: { type: "workarea" } }, () => (
                <WorkareaHeaderCell key={key} />
              ))
              .with(
                { x: { type: "formula" }, y: { type: "contract" } },
                ({ x: formula, y: contract }) => {
                  const formulaSum = getFormulaSum(
                    contract.data.contractId,
                    formula.data.id,
                    scheduleData
                  )
                  return formulaSum ? (
                    <FormulaContractCell key={key} formulaSum={formulaSum} />
                  ) : (
                    <div key={key}></div>
                  )
                }
              )
              .exhaustive()
          }}
        />
        <LoadingOverlay
          visible={isFetching && loadingCells.length === 0}
          overlayProps={{ blur: 0, backgroundOpacity: 0.4 }}
          loaderProps={{ type: "dots" }}
        />
      </Box>
    </Page>
  )
}
